// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React from "react";
import "./DocumentScanner.scss";
import { useTranslation } from "react-i18next";
import { storage } from '../../context/FirebaseConfig.js';
import { AuthContext } from '../../context/AuthContext';

import { Modal, Button } from 'react-bootstrap';

import ImageSquare from "../../shared_components/ImageSquare/ImageSquare";
import DocumentInfo from "./DocumentInfo";
import ZoomImage from "../ZoomImage/ZoomImage.jsx";

// Importing icons
import close_icon from '../../assets/icons/close_black.svg';
import rotate_icon from '../../assets/icons/black/rotate-left.png';

export default function DocumentScanner({ deliveryId, documentsData, show, onHide, handleDocumentsDataFieldChange }) {
    const { i18n, t } = useTranslation();
    const { logEventCustom } = React.useContext(AuthContext);
    const [selectedDocumentId, setSelectedDocumentId] = React.useState();
    const [animateRotation, setAnimateRotation] = React.useState(false);

    React.useEffect(() => {
        const numDocs = Object.keys(documentsData?.scanned_documents || {}).length;
        if (numDocs === 0) {
            setSelectedDocumentId(null);
        } else if (!(selectedDocumentId in documentsData?.scanned_documents)) {
            setSelectedDocumentId(Object.keys(documentsData?.scanned_documents || {})?.[numDocs-1]);
        }
    }, [Object.keys(documentsData?.scanned_documents || {})]);

    /// Scale factor for vertical rotations (90° or 270°)
    const scaleFactor = (documentsData?.scanned_documents?.[selectedDocumentId]?.image_rotation || 0) % 180 !== 0 ? 0.75 : 1;

    // Handler to rotate the image 90° clockwise
    function rotateImage() {
        // Enable rotation animation for the transition
        setAnimateRotation(true);
        setTimeout(() => setAnimateRotation(false), 3000);

        const rotation = documentsData?.scanned_documents?.[selectedDocumentId]?.image_rotation || 0;
        handleDocumentsDataFieldChange({
            scanned_documents: {
                [selectedDocumentId]: {
                    image_rotation: rotation-90
                }
            }
        })
    };

    function editDocumentField(e) {
        handleDocumentsDataFieldChange({
            "scanned_documents": {
                [selectedDocumentId]: {
                    [e.target.name]: e.target.value
                }
            },
            "complete_data": {
                [e.target.name]: e.target.value
            }
        });
        logEventCustom("edit_weighing_slip", {delivery_id: deliveryId, field: e.target.name, value: e.target.value});
    }
    function getScannedDocument() {
        return (
            <div className="document-scanner">
                <div className="close-button" onClick={onHide}>
                    <img className="close-button-icon" src={close_icon}/>
                </div>
                <div className="document-scanner-img-container">
                    <div
                        className={`rotate-container ${documentsData?.scanned_documents?.[selectedDocumentId]?.image_rotation % 180 !==0 ? "vertical-rotated": ""}`}
                        style={{
                        transform: `rotate(${documentsData?.scanned_documents?.[selectedDocumentId]?.image_rotation}deg) scale(${scaleFactor})`,
                        transition: animateRotation ? "transform 0.5s" : "none",
                        }}
                    >
                        <ZoomImage image_url={documentsData?.scanned_documents?.[selectedDocumentId]?.image_url} />
                    </div>
                    <Button
                        className="rotate-button"
                        variant="secondary"
                        onClick={rotateImage}>
                        <img src={rotate_icon} alt="rotate" />
                    </Button>
                </div>
                <div className="document-scanner-documents">
                    <div className="document-scanner-documents-card-wrapper">
                        {Object.entries(documentsData?.scanned_documents || {}).sort().map(([scannedDocumentId, scannedDocument]) => {
                            const isSelected = selectedDocumentId === scannedDocumentId;
                            return (
                                <div className={`document-square-wrapper ${isSelected && "selected"}`} 
                                    onClick={() => setSelectedDocumentId(scannedDocumentId)}
                                    key={scannedDocumentId}
                                >
                                    <ImageSquare key={scannedDocumentId}
                                        storage={storage} 
                                        imageUrl={scannedDocument?.image_url} 
                                        icons={[]}
                                    />
                                    {!isSelected && <div className="document-square-overlay"></div>}
                                </div>
                            )
                        })}
                    </div>
                </div>
                {selectedDocumentId && <DocumentInfo
                    documentsData={documentsData?.scanned_documents?.[selectedDocumentId]}
                    editDocumentField={editDocumentField}
                />}
            </div>
        )
    }    

    return (
        <Modal 
            show={show}
            onHide={onHide}
            backdrop = "static"
            className = "modal"
        >
            <div className={`modal-box document-scanner-modal-box`}>
                {getScannedDocument()}
            </div>
        </Modal>
    )
}